// These first few constants are the chart types for users, regardless of
// what ZingCharts needs.
export const BULLET_CHART_TYPE = "bullet";
export const HORIZONTAL_BULLET_CHART_TYPE = "horizontal-bullet";
export const LINE_CHART_TYPE = "line";
export const KAPLAN_MEIER_CHART_TYPE = "kaplan-meier";
export const STACKED_BULLET_CHART_TYPE = "stacked-bullet";
export const HORIZONTAL_STACKED_BULLET_CHART_TYPE = "horizontal-stacked-bullet";

export const HORIZONTAL_CHART_TYPES = [ 
  HORIZONTAL_BULLET_CHART_TYPE, 
  HORIZONTAL_STACKED_BULLET_CHART_TYPE 
];

export const STACKED_CHART_TYPES = [ 
  STACKED_BULLET_CHART_TYPE, 
  HORIZONTAL_STACKED_BULLET_CHART_TYPE 
];

// The next few constants are the types of charts ZingCharts knows about.
export const BAR_ZINGCHART_TYPE = "bar";
export const HORIZONTAL_BAR_ZINGCHART_TYPE = "hbar";
export const BULLET_ZINGCHART_TYPE = "bullet";
export const HORIZONTAL_BULLET_ZINGCHART_TYPE = "hbullet";
export const LINE_ZINGCHART_TYPE = "line";
export const VERTICAL_LINE_ZINGCHART_TYPE = "vline";
export const MIXED_ZINGCHART_TYPE = "mixed";
export const HORIZONTAL_MIXED_ZINGCHART_TYPE = "hmixed";

export function postRenderAdjustments(chart, props, zingChartExec) {
  // Get the info about the y-axis in the chart that has already been rendered.
  let scaleYInfo = zingChartExec(chart.id, 'getobjectinfo', {
    object: 'scale',
    name: 'scale-y'
  });


  if (scaleYInfo) {
    // Use the y-axis info to reposition the ends of the better arrow.
    let { from, to } = setYArrowDetails(props.type, scaleYInfo, props.betterArrowDirection);

    zingChartExec(chart.id, 'updateobject', {
      type: 'arrow',
      id: props.id + '-better-arrow',
      data: { from, to }
    });
  }
  
  // Start of x-axis label cut off adjustment
  let plotInfo = zingChartExec(chart.id, 'getobjectinfo', {
    object: 'graph'
  });
  
  // Get the info about the y-axis in the chart that has already been rendered.
  let scaleXInfo = zingChartExec(chart.id, 'getobjectinfo', {
    object: 'scale',
    name: 'scale-x'
  });
  
  if (scaleXInfo && scaleXInfo.label && plotInfo) {
    let breakText = "<br/><br/>";
    
    let bottomOfLabel = scaleXInfo.label.height + scaleXInfo.label.y;

    if (scaleXInfo.text.indexOf(breakText) === -1 && bottomOfLabel > plotInfo.height) {
      let chartData = zingChartExec(chart.id, 'getdata');
      
      chartData['graphset'][0]['scale-x']['label']['text'] = scaleXInfo.text + breakText;
      
      zingChartExec(chart.id, 'setdata', { data: chartData });
    }
  }
  // End of x-axis label cut off adjustment
}


// inserts breaks before cohort values in x axis labels
export function insertBreaks(labels) {
  let xBreakHappens = false;

  let xAxisLabels = labels.map((label) => {
        if (label.indexOf("N = ") >= 0) {
          xBreakHappens = true;
        }
        return label.replace(/(N = )/, '<br/> $1').trim();
      });

  return { xAxisLabels, xBreakHappens };
}

// figures out which x axis label provided is the longest and returns it
export function determineXAxisLongestLength(labels) {
  return labels.reduce((highestLen, currentString) => {
    let currentStringLength = currentString.split(/(N = )/)[0].length;

    return (highestLen > currentStringLength) ?
      highestLen :
      currentStringLength;
  }, 0);
}

// determines how much width to give the x axis labels in mobile view
export function setXAxisMobileWidth(xAxisLongestLength) {
  return (xAxisLongestLength < 6) ?
    40 :
    20 * (Math.floor((xAxisLongestLength - 2) / 4) + 2);
}

// adds the object needed for the better arrow to the chart data
export function addBetterArrow(id, chartType, chartData, mobileBP, yAxisLabel, betterArrowDirection, arrowColor, fontColor) {
  let top, bottom;
  
  if (!HORIZONTAL_CHART_TYPES.includes(chartType)) {
    chartData["scale-y"]["label"]["padding-right"] = 20;
    chartData["scale-y"]["label"]["padding-left"] = 20;
    chartData["scale-y"]["label"]["padding-top"] = undefined;

    top = {
      x: 8,
      y: "27%"
    };

    bottom = {
      x: 8,
      y: "70%"
    };
  }
  else {
    top = {
      x: "70%",
      y: 40
    };

    bottom = {
      x: "27%",
      y: 40
    };
  }

  chartData["arrows"] = [{
    id: id + "-better-arrow",
    text: "Better",
    backgroundColor: arrowColor,
    borderColor: arrowColor,
    fontColor: fontColor,
    fontAngle: (!HORIZONTAL_CHART_TYPES.includes(chartType)) ? 270 : 0,
    fontSize: 10,
    mediaRules: [
      {
        maxWidth: mobileBP,
        fontSize: 8
      }
    ],
    from: (betterArrowDirection === "down") ? top : bottom,
    to: (betterArrowDirection === "down") ? bottom : top
  }];

  // Adds margin to the left if the yAxisLabel is missing
  if (!yAxisLabel) {
    chartData["scale-y"]["label"]["text"] = ".";
    chartData["scale-y"]["label"]["fontColor"] = "#fff";
    chartData["scale-y"]["label"]["padding-top"] = 0;
    chartData["scale-y"]["label"]["padding-bottom"] = 0;
  }

  return chartData;
}

// adds an overall benchmark for charts using one
export function addOverallBenchmark(chartType, mappedChartType, chartData, showLegend, benchmarkName, benchmarkValue, benchmarkColor) {
  let benchmarkTooltipText = benchmarkName ?
    benchmarkName + "<br />" :
    "";

  chartData.plot = Object.assign(
    chartData.plot,
    {
      "bar-width": "100%",
      barsSpaceLeft: "25%",
      barsSpaceRight: "25%",
      "bar-space": 3
    }
  );

  let benchmarkObject = {
    "type": (HORIZONTAL_CHART_TYPES.includes(chartType)) ? VERTICAL_LINE_ZINGCHART_TYPE : LINE_ZINGCHART_TYPE,
    values: [benchmarkValue, benchmarkValue],
    text: benchmarkTooltipText,
    marker: {
      visible: false
    },
    "scales": (!STACKED_CHART_TYPES.includes(chartType)) ? "scale-x-2,scale-y" : "scale-x-2,scale-y-2",
    "zIndex": 5,
    "line-color": benchmarkColor,
    "line-width": 3,
    "legend-item": {
      flat: true,
      visible: benchmarkTooltipText
    },
    "legend-marker": {
      "line-color": benchmarkColor,
      "line-width": 2,
      "type": "none",
      "show-line":true
    }
  };

  chartData["series"].push(benchmarkObject);

  return chartData;
}

// adds 0s onto the chart in cases where the value is 0 instead of not being provided
export function showZeroes(chartData, isStacked, mobileFontSize) {
  let value = (isStacked) ? "%total" : "%v";

  chartData["plot"]["value-box"] = {
    "text": value,
    "rules": [
      {
        "rule": value + " == 0",
        "placement": "top",
        mediaRules: [
          {
            maxWidth: 450,
            "font-size": mobileFontSize
          }
        ]
      },
      {
        "rule": value + " != 0",
        "visible": "false"
      }
    ]
  };
}

// creates an object to be added to the series part of the chart json
export function buildSeriesObject(chartType, mappedChartType,
  seriesName, seriesValues, benchmarkName, benchmarkValues,
  color, benchmarkColor, fontFamily, hasOverallBenchmark) {
  // Initial series object
  let seriesObject = {
    type: mappedChartType,
    text: seriesName,
    values: seriesValues,
    "legend-item": {
      visible: seriesName
    }
  };

  if (mappedChartType === BULLET_ZINGCHART_TYPE || mappedChartType === HORIZONTAL_BULLET_ZINGCHART_TYPE) {
    let benchmarkTooltipText = benchmarkName ?
      benchmarkName + "<br />" :
      "";

    seriesObject = Object.assign(
      seriesObject,
      {
        "background-color": color,
        goal: (benchmarkValues && benchmarkValues.length && benchmarkValues.filter(value => value !== null).length) ? {
          height: 6,
          "background-color": benchmarkColor,
          "tooltip":{
            "text": benchmarkTooltipText + "<span style='font-size: 14px;'>%g</span>",
            "font-family": fontFamily,
            "background-color": benchmarkColor
          }
        } : null,
        goals: (benchmarkValues && benchmarkValues.length && benchmarkValues.filter(value => value !== null).length) ? benchmarkValues : null
      }
    );

    if (hasOverallBenchmark) {
      seriesObject.type = (mappedChartType === HORIZONTAL_BULLET_ZINGCHART_TYPE) ? HORIZONTAL_BAR_ZINGCHART_TYPE : BAR_ZINGCHART_TYPE;
    }
  }
  // Adds series styles for line charts.
  else {
    seriesObject = Object.assign(
      seriesObject,
      {
        "line-color": color,
        "line-width": 2,
        marker: {
          "background-color": color
        }
      }
    );
  }
  return seriesObject;
}

// creates a series object for individual benchmarks in bullet charts that need legend items
export function buildBenchmarkLegendObject(chartType, mappedChartType, benchmarkName, benchmarkColor) {
  let benchmarkObject = null;

  // Adds benchmarks to legend for bullet charts
  if (mappedChartType === BULLET_ZINGCHART_TYPE || mappedChartType === HORIZONTAL_BULLET_ZINGCHART_TYPE) {
    // If there are benchmark titles on a bullet chart, they are added to the legend through series elements
    if (benchmarkName && benchmarkName.length) {
      benchmarkObject = {
        values: [],
        text: benchmarkName,
        "legend-item": {
          flat: true
        },
        "legend-marker": {
          "line-color": benchmarkColor,
          "line-width": 2,
          "type": "none",
          "show-line":true
        }
      };

      if (
        chartType === BULLET_CHART_TYPE || 
        chartType === HORIZONTAL_BULLET_CHART_TYPE || 
        chartType === HORIZONTAL_STACKED_BULLET_CHART_TYPE
      ) {
        benchmarkObject.scales = "scale-x-2,scale-y";
      }
    }
  }

  return benchmarkObject;
}


export function setYArrowDetails(chartType, scaleYInfo, betterArrowDirection) {
  const arrowSpace = (!HORIZONTAL_CHART_TYPES.includes(chartType)) ? scaleYInfo.height * .15 : scaleYInfo.width * .15;
  let top, bottom;

  if (!HORIZONTAL_CHART_TYPES.includes(chartType)) {
    top = {
      x: 8,
      y: scaleYInfo.y + arrowSpace
    };
    bottom = {
      x: 8,
      y: scaleYInfo.y + scaleYInfo.height - arrowSpace
    };
  }
  else {
    top = {
      x: scaleYInfo.x + scaleYInfo.width - arrowSpace,
      y: scaleYInfo.y - 15
    };
    bottom = {
      x: scaleYInfo.x + arrowSpace,
      y: scaleYInfo.y - 15
    };
  }

  let from = (betterArrowDirection === "down") ? top : bottom;
  let to = (betterArrowDirection === "down") ? bottom : top;

  return { from, to };
}

export function yMinMaxBuilder(props, seriesValues) {
  let yMax = props.yMax;

  // if chart is a stacked bullet chart without a set yMax value already
  if (STACKED_CHART_TYPES.includes(props.type) && (!yMax && yMax !== 0)) {
    // get the sum for each stack, or default to 1 if there's no series values
    let xAxisSums = (seriesValues.length) ?
      seriesValues[0].map((value, index) => {
        return seriesValues.reduce((sum, series) => sum + (series[index] ? parseFloat(series[index]) : 0), 0);
      }) :
      [1];

    // figure out which stack is the tallest and set that value as the yMax for the chart
    yMax = xAxisSums.reduce((largest, xAxisSum) => {
      return (xAxisSum > largest) ? xAxisSum : largest;
    }, 0);
  }

  let ySteps = "";

  const yDiff = (props.yMin && yMax) ?
    yMax - props.yMin :
    null;

  // Fix for the fact that having a difference less than 1 without steps breaks the chart
  if (yDiff && yDiff > 0 && yDiff < 1) {
    // Figures out how many digits after the decimal
    const yStepValue = (props.yMin.toString().length >= yMax.toString().length) ?
      props.yMin.toString().split('.')[1].length :
      yMax.toString().split('.')[1].length;

    ySteps = ":0." + "0".repeat(yStepValue - 1) + "1";
  }
  else {
    ySteps = (props.ySteps) ? ":" + props.ySteps : "";
  }

  let yMinMax = ((props.yMin || typeof props.yMin === "number") && (props.yMax || typeof props.yMax === "number")) ?
    {
      "values": props.yMin + ":" + yMax + ySteps
    } :
    {
      "min-value": ((props.yMin !== "") && (props.yMin !== yMax)) ? props.yMin : undefined,
      "max-value": ((props.yMax !== "") && (props.yMin !== yMax)) ? yMax : undefined,
      "step": (props.ySteps !== "") ? props.ySteps : undefined
    };

  return yMinMax;
}

export function dataOrganizer(data) {
  let seriesNames = [],
      seriesBenchmarkNames = [],
      xAxisLabels = [],
      seriesValues = [],
      seriesBenchmarks = [];

  // Figures out how many series have to be created
  let numberOfSeries = data.reduce((maxLength, dataRow) => {
    // - 1 to remove x axis title, / 2 to remove benchmarks
    return (Math.ceil((dataRow.length - 1) / 2) > maxLength) ?
      Math.ceil((dataRow.length - 1) / 2):
      maxLength;
  }, 0);

  // Creates array of blank arrays
  seriesValues = new Array(numberOfSeries).fill(null).map(() => { return []; });
  seriesBenchmarks = new Array(numberOfSeries).fill(null).map(() => { return []; });

  data.forEach((dataRow, rowIndex) => {
    // First row, series + bookmark titles
    if (rowIndex === 0) {
      dataRow.forEach((dataCell, cellIndex) => {
        // First cell is ignored
        if (cellIndex !== 0) {
          // Even indexes are benchmark titles
          if (cellIndex % 2 === 0) {
            seriesBenchmarkNames.push(dataCell);
          }
          // Odd indexes are series titles
          else {
            let formattedDataCell = (dataCell !== "") ?
            dataCell + "<br />" :
            dataCell;

            seriesNames.push(formattedDataCell);
          }
        }
      });
    }
    // Second row onwards
    else {
      dataRow.forEach((dataCell, cellIndex) => {
        // First column goes into xAxisLabels
        if (cellIndex === 0) {
          xAxisLabels.push(dataCell);
        }
        // Odd columns are series values
        else if (cellIndex % 2 !== 0) {
          seriesValues[(cellIndex - 1) / 2].push((!isNaN(parseFloat(dataCell))) ? parseFloat(dataCell) : null);
        }
        // Even columns are benchmark values
        else {
          seriesBenchmarks[(cellIndex / 2) - 1].push((!isNaN(parseFloat(dataCell))) ? parseFloat(dataCell) : null);
        }
      });
    }
  });

  return {
    seriesNames: seriesNames,
    seriesBenchmarkNames: seriesBenchmarkNames,
    xAxisLabels: xAxisLabels,
    seriesValues: seriesValues,
    seriesBenchmarks: seriesBenchmarks
  };
}

// Takes the props passed in and makes a data object for zingcharts to store in state
export function dataBuilder (props) {
  let newChartData = {};

  // chartTypeMap maps from the way Cleveland Clinic people name charts to
  // the way ZingCharts names them.
  let chartTypeMap = {};
  chartTypeMap[BULLET_CHART_TYPE] = BULLET_ZINGCHART_TYPE;
  chartTypeMap[LINE_CHART_TYPE] = LINE_ZINGCHART_TYPE;
  chartTypeMap[KAPLAN_MEIER_CHART_TYPE] = LINE_ZINGCHART_TYPE;
  chartTypeMap[HORIZONTAL_BULLET_CHART_TYPE] = HORIZONTAL_BULLET_ZINGCHART_TYPE;
  chartTypeMap[HORIZONTAL_STACKED_BULLET_CHART_TYPE] = HORIZONTAL_BULLET_ZINGCHART_TYPE;
  chartTypeMap[STACKED_BULLET_CHART_TYPE] = BULLET_ZINGCHART_TYPE;

  // Data will stay blank if no type is provided
  if (props.type) {
    // Reused colors
    const black = "#4b4b45";
    const grey = "#7b7d83";
    const white = "#fff";
    const colorScheme = [ "#0078bf", "#384d5f", "#00843d", "#79d0e6", "#5c2161", "#a61f56" ];
    const benchmarkColorScheme = [ "#f08122", "#79d0e6", "#384d5f", "#a61f56", "#0078bf", "#00843d" ];

    // Reused values
    const fontFamily = "Source Sans Pro, Lato, Helvetica, Arial";
    const mobileBP = 350;
    const mobileFontSize = 8;
    const mobileLabelSize = 9;

    let seriesNames = [],
      seriesBenchmarkNames = [],
      xAxisLabels = [],
      seriesValues = [],
      seriesBenchmarks = [],
      xAxisLongestLength = 0;

    // Populates base data based on type provided
    newChartData = {
      gui: {
        behaviors: [
          {
            id: 'DownloadPDF',
            enabled: 'none'
          },
          {
            id: 'DownloadSVG',
            enabled: 'none'
          },
          {
            id: 'DownloadCSV',
            enabled: 'none'
          },
          {
            id: 'DownloadXLS',
            enabled: 'none'
          },
          {
            id: 'Print',
            enabled: 'none'
          },
          {
            id: 'ViewDataTable',
            enabled: 'none'
          },
          {
            id: 'ViewSource',
            enabled: 'none' // Don't enable outside of dev
          }
        ],
        "context-menu": {
          button: {
            visible: false
          }
        }
      },
      type: (HORIZONTAL_CHART_TYPES.includes(props.type)) ? HORIZONTAL_MIXED_ZINGCHART_TYPE : MIXED_ZINGCHART_TYPE,
      "plotarea": {
        "margin-top": "10%",
        "margin-left": "dynamic",
        "margin-right": "dynamic",
        "margin-bottom": "dynamic"
      },
      plot: {
        tooltip: {
          text: "%t<span style='font-size: 14px;'>%v</span>",
          "font-family": fontFamily
        }
      },
    };

    if (props.data && props.data.length) {
      const organizedData = dataOrganizer(props.data);

      seriesNames = organizedData.seriesNames;
      seriesBenchmarkNames = organizedData.seriesBenchmarkNames;
      xAxisLabels = organizedData.xAxisLabels;
      xAxisLongestLength = determineXAxisLongestLength(xAxisLabels);
      seriesValues = organizedData.seriesValues;
      seriesBenchmarks = organizedData.seriesBenchmarks;
    }

    // Better arrow height and x axis mobile width adjustments based on presence of legend and width of x axis labels
    let xAxisMobileWidth = setXAxisMobileWidth(xAxisLongestLength);

    // X Axis Labels break insertion for cohorts
    let insertedBreaksObject = insertBreaks(xAxisLabels);
    xAxisLabels = insertedBreaksObject.xAxisLabels;
    let { xBreakHappens } = insertedBreaksObject;

    // Populates base data based on type provided
    newChartData = Object.assign({
      legend: {
        visible: (props.showLegend && (seriesNames.filter(value => value).length || seriesBenchmarkNames.filter(value => value).length)),
        "toggle-action": "disabled",
        "adjust-layout": true,
        "border-color": grey,
        "border-width": 1,
        "padding-top": 10,
        "padding-left": 3,
        "padding-bottom": 13,
        "padding-right": 3,
        mediaRules: [
          {
            maxWidth: mobileBP,
            width: "99.5%", //setting this to 100% breaks the chart
            layout: "2x2",
            "max-items": 4,
            overflow: "page",
            "page-status": {
              "font-size": 8,
              "offset-y": -5,
              height: 20
            },
            "page-on": {
              size: 13,
              "offset-y": -5
            },
            "page-off": {
              size: 13,
              "offset-y": -5
            }
          },
          {
            maxWidth: 450,
            "border-color": black
          }
        ],
        item: {
          "font-family": fontFamily,
          "font-size": 10,
          mediaRules: [
            {
              maxWidth: mobileBP,
              "font-size": mobileLabelSize
            }
          ]
        }
      },
      "scale-x": {
        itemsOverlap: true,
        maxItems: 9999,
        label: {
          text: props.xAxisLabel,
          color: black,
          "wrap-text": true,
          width: "100%",
          "font-family": fontFamily,
          mediaRules: [
            {
              maxWidth: mobileBP,
              "font-size": mobileLabelSize
            }
          ]
        },
        labels: xAxisLabels,
        "line-color": grey,
        mediaRules: [
          {
            maxWidth: 450,
            "line-color": black
          }
        ],
        item: {
          wrapText: true,
          width: (HORIZONTAL_CHART_TYPES.includes(props.type)) ? "75px" : '100%',
          "font-family": fontFamily,
          mediaRules: [
            {
              maxWidth: 450,
              "font-size": mobileFontSize,
              fontAngle: (HORIZONTAL_CHART_TYPES.includes(props.type)) ? 0 : 270,
              "text-align": "right",
              "width": (HORIZONTAL_CHART_TYPES.includes(props.type)) ? "55px" : xAxisMobileWidth,
              color: black
            }
          ]
        },
        tick: {
          mediaRules: [
            {
              maxWidth: 450,
              "line-color": black
            }
          ]
        }
      },
      "scale-y": {
        "label": {
          "text": props.yAxisLabel,
          color: black,
          "wrap-text": true,
          "padding-left": -12,
          "padding-top": (HORIZONTAL_CHART_TYPES.includes(props.type)) ? 0 : -5,
          width: "100%",
          "font-family": fontFamily,
          mediaRules: [
            {
              maxWidth: mobileBP,
              "font-size": mobileLabelSize
            }
          ]
        },
        "line-color": grey,
        mediaRules: [
          {
            maxWidth: 450,
            "line-color": black
          }
        ],
        item: {
          "font-family": fontFamily,
          mediaRules: [
            {
              maxWidth: 450,
              "font-size": mobileFontSize,
              color: black
            }
          ]
        },
        tick: {
          mediaRules: [
            {
              maxWidth: 450,
              "line-color": black
            }
          ]
        }
      },
      "scaleX2": { // Second scale for the benchmark legend elements to exist on
        visible: false
      },
      "scaleY2": { // Second scale for the benchmark legend elements to exist on
        visible: false
      },
      series: []
    },
    newChartData);

    // Shows legend across top of chart instead of on the side
    if(props.legendOnTop) {
      const legendOnTop = {
        width: "94%",
        layout: "2x2",
        "max-items": 4,
        overflow: "page",
        "page-status": {
          "font-size": 8
        }
      };

      newChartData["legend"] = Object.assign(legendOnTop, newChartData["legend"]);
    }

    if (xBreakHappens) {
      newChartData["scale-x"]["label"]["padding-top"] = 20;
      newChartData["scale-x"]["label"]["mediaRules"][0]["padding-top"] = 0;
    }

    newChartData["scale-y"] = Object.assign(newChartData["scale-y"], yMinMaxBuilder(props, seriesValues));
    newChartData["scaleY2"] = Object.assign(newChartData["scaleY2"], yMinMaxBuilder(props, seriesValues));

    // Better Arrow logic
    if (props.betterArrowDirection) {
      newChartData = addBetterArrow(
        props.id,
        props.type,
        newChartData,
        mobileBP,
        props.yAxisLabel,
        props.betterArrowDirection,
        grey,
        white
      );
    }

    // Make a Kaplan-Meier chart different from a line chart.
    if (props.type === KAPLAN_MEIER_CHART_TYPE) {
      newChartData.plot = Object.assign(newChartData.plot,
        {
          aspect: "stepped",
          marker: {
            visible: false
          }
        });

      // Logic for removing ticks and making legend labels show
      newChartData["scale-x"]["tick"] = { visible: false };
      newChartData["scale-x"]["items-overlap"] = true;
      newChartData["scale-x"]["item"]["width"] = null;
      newChartData["scale-x"]["item"]["wrapText"] = false;
    }

    // Make a stacked bullet chart different from a plain bullet chart and reverse legend.
    if (STACKED_CHART_TYPES.includes(props.type)) {
      newChartData.plot = Object.assign(newChartData.plot,
        {
          stacked: true
        });

      newChartData["legend"]["reverseSeries"] = true;
    }

    // Adds logic for showing 0s to bullet charts
    if (
      props.type === STACKED_BULLET_CHART_TYPE ||
      props.type === BULLET_CHART_TYPE ||
      props.type === HORIZONTAL_BULLET_CHART_TYPE ||
      props.type === HORIZONTAL_STACKED_BULLET_CHART_TYPE
    ) {
      showZeroes(newChartData,
        STACKED_CHART_TYPES.includes(props.type),
        mobileFontSize
      );
    }

    // Adds new series for each series name
    if (seriesNames.length) {
      if (STACKED_CHART_TYPES.includes(props.type) &&
        props.overallBenchmark) {
        // Overall benchmark logic for stacked bar charts
        newChartData = addOverallBenchmark(props.type,
          chartTypeMap[props.type],
          newChartData,
          props.showLegend,
          seriesBenchmarkNames[0],
          seriesBenchmarks[0][0],
          benchmarkColorScheme[0]
        );
      }

      seriesNames.forEach((seriesName, index) => {
        let seriesObject = buildSeriesObject(
          props.type,
          chartTypeMap[props.type],
          seriesName,
          seriesValues[index],
          seriesBenchmarkNames[index],
          seriesBenchmarks[index],
          colorScheme[index % colorScheme.length],
          benchmarkColorScheme[index % benchmarkColorScheme.length],
          fontFamily,
          props.overallBenchmark
        );

        let benchmarkObject = (!props.overallBenchmark) ? buildBenchmarkLegendObject(
          props.type,
          chartTypeMap[props.type],
          seriesBenchmarkNames[index],
          benchmarkColorScheme[index % benchmarkColorScheme.length]
        ) : null;

        if (STACKED_CHART_TYPES.includes(props.type) && benchmarkObject) {
          newChartData["series"].push(benchmarkObject);
        }
        newChartData["series"].push(seriesObject);
        if (!STACKED_CHART_TYPES.includes(props.type) && benchmarkObject) {
          newChartData["series"].push(benchmarkObject);
        }
      });
    }
    // Needs a series object to populate the chart at all
    else {
      newChartData["series"].push({});
    }

    // Overall benchmark logic for non-stacked bullet charts
    if ((chartTypeMap[props.type] === LINE_ZINGCHART_TYPE ||
      (props.overallBenchmark &&
       (
        props.type === BULLET_CHART_TYPE ||
        props.type === HORIZONTAL_BULLET_CHART_TYPE
       )
      )) &&
      seriesBenchmarks.length &&
      seriesBenchmarks[0][0] !== null
    ) {
      newChartData = addOverallBenchmark(props.type,
        chartTypeMap[props.type],
        newChartData,
        props.showLegend,
        seriesBenchmarkNames[0],
        seriesBenchmarks[0][0],
        benchmarkColorScheme[0]
      );
    }
  }

  return newChartData;
}



// WEBPACK FOOTER //
// ./src/shared/components/presentation/chart/chartFunctions.js